// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, finalize, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TagsSlice from "../slice"
import type { RootState } from "../../../app/store"
import { showSnackBar } from "../../snackbar/actions"
import { Code, from, Init, isConnectError, tagService } from "../../../app/rpc"
import { GetTagRequestSchema } from "generated/legebill/site/tag/v4/tag_pb"
import { Tag } from "generated/legebill/type/tag_pb"

type GetTagRpc = (request: Init<typeof GetTagRequestSchema>) => Observable<Tag>

// Track in-flight requests to prevent duplicates
const inFlightRequests = new Set<string>()

// Export for testing
export const __test__ = { inFlightRequests }

export const getTag = (getTagRpc: GetTagRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.getTag.match),
      withLatestFrom(state$),
      mergeMap(([{ payload: { request, swallowFailure }}, state]) => {
        const { tagId } = request
        // Skip if tag already in cache or request is already in flight
        if (!tagId || state.tags.tagsCache?.[tagId] || inFlightRequests.has(tagId)) {
          return EMPTY
        }

        // Mark as in flight
        inFlightRequests.add(tagId)

        return of(request)
          .pipe(
            mergeMap(getTagRpc),
            map(t => t),
            map(TagsSlice.setTag),
            catchError(e => handleError(e, tagId, swallowFailure)),
            // Clean up in-flight tracking on completion or error
            finalize(() => inFlightRequests.delete(tagId))
          )
      })
    )

const getTagRpc = (request: Init<typeof GetTagRequestSchema>): Observable<Tag> =>
  from(() => tagService.getTag(create(GetTagRequestSchema, request)))

const handleError = (e: unknown, tagId: string, swallowFailure: boolean | undefined) => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      const actions: Action[] = [
        showSnackBar({
          message: t({ id: "msg_get_tag_not_found", message: "Tag not found. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
          messageType: "ERROR"
        }),
        TagsSlice.removeFromCache(tagId),
      ]

      if (swallowFailure) {
        actions.shift()
      }

      return concat(actions)
    }

    if (code === Code.PermissionDenied) {
      const actions: Action[] = [
        showSnackBar({
          message: t({ id: "msg_get_tag_permission_denied", message: "Failed to load tag, permission denied. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
          messageType: "ERROR"
        }),
      ]

      if (swallowFailure) {
        actions.shift()
      }

      return concat(actions)
    }
  }

  const actions = [
    showSnackBar({
      message: t({ id: "msg_get_tag_failed", message: "Failed to load tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
      messageType: "ERROR",
    }),
  ]

  if (swallowFailure) {
    actions.shift()
  }

  return concat(actions)
}

export default getTag(getTagRpc)
